import React, { createContext, useContext, useMemo, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Layout, Menu, Input, Avatar, Badge, Dropdown, Button, Grid, theme } from 'antd';
import type { MenuProps } from 'antd';
import {
  AppstoreOutlined, FolderOpenOutlined, FireOutlined, ThunderboltOutlined, ApiOutlined,
  CloudOutlined, ColumnWidthOutlined, BankOutlined, CalendarOutlined,
  SafetyCertificateOutlined, TeamOutlined, FolderOutlined, BellOutlined,
  SearchOutlined, LogoutOutlined, DownOutlined, QuestionCircleOutlined,
  MessageOutlined,
} from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';
import { useOrg } from '../context/OrgContext';
import { TOOLS, GROUP_LABELS, ToolGroup, ToolId, isToolEnabled } from '../config/tools';
import OfflineBanner from './OfflineBanner';
import InviteConflictBanner from './InviteConflictBanner';

/* Lightweight context so content pages (e.g. ProjectHub) can set the topbar title. */
interface ShellTitle { title: string; setTitle: (t: string) => void; }
const ShellTitleCtx = createContext<ShellTitle>({ title: '', setTitle: () => {} });
export const useShellTitle = () => useContext(ShellTitleCtx);

/* v2 mark (magnifying glass over a green/teal/blue skyline — see
   eco-build-portfolio/brand/sp_mark_v2_mono.svg for the full master), one-colour variant: every
   fill/stroke is currentColor, so `color` on the <svg> controls it — light for this dark sider. */
const BrandMark: React.FC<{ size?: number }> = ({ size = 32 }) => (
  <svg width={size} height={size} viewBox="0 0 128 128" aria-hidden="true" style={{ color: '#ECEFF1' }}>
    <defs>
      <mask id="spf-glass">
        <rect x="0" y="0" width="128" height="128" fill="#ffffff" />
        <circle cx="74" cy="60" r="26.5" fill="#757575" />
        <circle cx="74" cy="60" r="30.5" fill="none" stroke="#000000" strokeWidth="8" />
        <line x1="53" y1="81" x2="21" y2="113" stroke="#000000" strokeWidth="20" strokeLinecap="round" />
      </mask>
    </defs>
    <g mask="url(#spf-glass)" fill="currentColor">
      <line x1="2" y1="92" x2="126" y2="92" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
      <rect x="25.2" y="78" width="2.2" height="14" />
      <circle cx="22" cy="74" r="5.6" />
      <circle cx="27.4" cy="71.6" r="6.4" />
      <circle cx="23.6" cy="67.8" r="4.8" />
      <rect x="57" y="16" width="22" height="76" />
      <rect x="86" y="32" width="16" height="60" />
      <rect x="45" y="34" width="14" height="58" />
      <rect x="32" y="58" width="15" height="34" />
      <rect x="100" y="60" width="14" height="32" />
    </g>
    <line x1="53" y1="81" x2="21" y2="113" stroke="currentColor" strokeWidth="14" strokeLinecap="round" />
    <circle cx="74" cy="60" r="29" fill="none" stroke="currentColor" strokeWidth="5" />
  </svg>
);

const initialsOf = (name?: string | null, email?: string | null): string => {
  const src = (name || email || '').trim();
  if (!src) return '··';
  if (name && name.trim()) {
    const parts = name.trim().split(/\s+/);
    return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || src[0].toUpperCase();
  }
  return src.slice(0, 2).toUpperCase();
};

const TOOL_ICONS: Record<ToolId, React.ReactNode> = {
  overview: <AppstoreOutlined />,
  documents: <FolderOpenOutlined />,
  boiler: <FireOutlined />,
  power: <ThunderboltOutlined />,
  powerFactor: <ApiOutlined />,
  ebtron: <CloudOutlined />,
  dalt: <ColumnWidthOutlined />,
  facility: <BankOutlined />,
  pmScheduler: <CalendarOutlined />,
  compliance: <SafetyCertificateOutlined />,
  assistant: <MessageOutlined />,
};

const GROUP_ORDER: ToolGroup[] = ['workspace', 'calc', 'manage'];

const AppShell: React.FC = () => {
  const { currentUser, logout } = useAuth();
  const { organization, isContractor } = useOrg();
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();
  // Below lg the sider is collapsed, so the content owns the full width — 24px each side
  // is a costly 48 of an iPad's 768.
  const contentPad = screens.lg ? 24 : 12;

  const [title, setTitle] = useState('');

  const projectMatch = location.pathname.match(/^\/projects\/([^/]+)/);
  const projectId = projectMatch ? projectMatch[1] : null;
  const activeTab = new URLSearchParams(location.search).get('tab') || 'overview';
  const enabled = organization?.enabledModules;

  const handleLogout = async () => { await logout(); navigate('/login'); };

  // Sidebar menu. Workspace links are global; calc/manage appear only inside a project.
  const menuItems: MenuProps['items'] = useMemo(() => {
    const groups: MenuProps['items'] = [];
    for (const group of GROUP_ORDER) {
      if (group !== 'workspace' && !projectId) continue;
      const children: MenuProps['items'] = [];
      if (group === 'workspace') {
        children.push({ key: 'nav:/', icon: <FolderOutlined />, label: 'Projects' });
        // Team management is not available to contractors.
        if (!isContractor) children.push({ key: 'nav:/team', icon: <TeamOutlined />, label: 'Team' });
        children.push({ key: 'nav:/help', icon: <QuestionCircleOutlined />, label: 'Help' });
      }
      if (projectId) {
        // Contractors only get the facility assessment tab.
        for (const t of TOOLS.filter(t => t.group === group && isToolEnabled(t.id, enabled)
            && (!isContractor || t.id === 'facility'))) {
          children.push({ key: `tab:${t.id}`, icon: TOOL_ICONS[t.id], label: t.label });
        }
      }
      if (children.length) groups.push({ type: 'group', label: GROUP_LABELS[group], children });
    }
    return groups;
  }, [projectId, enabled, isContractor]);

  const selectedKey = projectId
    ? `tab:${activeTab}`
    : location.pathname === '/team' ? 'nav:/team'
    : location.pathname === '/help' ? 'nav:/help'
    : 'nav:/';

  const onMenuClick: MenuProps['onClick'] = ({ key }) => {
    if (key.startsWith('nav:')) navigate(key.slice(4));
    else if (key.startsWith('tab:')) navigate(`/projects/${projectId}?tab=${key.slice(4)}`);
  };

  const accountMenu: MenuProps['items'] = [
    {
      key: 'who', disabled: true,
      label: (
        <div style={{ lineHeight: 1.4 }}>
          <div style={{ fontWeight: 600, color: token.colorText }}>{currentUser?.displayName || currentUser?.email}</div>
          {organization && <div style={{ fontSize: 12, color: token.colorTextTertiary }}>{organization.name}</div>}
        </div>
      ),
    },
    { type: 'divider' },
    { key: 'team', icon: <TeamOutlined />, label: 'Team' },
    { key: 'logout', icon: <LogoutOutlined />, label: 'Log out' },
  ];

  const onAccountClick: MenuProps['onClick'] = ({ key }) => {
    if (key === 'team') navigate('/team');
    if (key === 'logout') handleLogout();
  };

  return (
    <ShellTitleCtx.Provider value={{ title, setTitle }}>
      <Layout style={{ minHeight: '100vh' }}>
        <Layout.Sider width={232} breakpoint="lg" collapsedWidth={0}>
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 16, height: 64, textDecoration: 'none' }}>
            <BrandMark />
            <div>
              <div style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700, fontSize: 17, lineHeight: 1, color: '#fff' }}>
                mep<span style={{ color: '#A5D6A7' }}>-tools</span>
              </div>
              <div style={{ fontSize: 9.5, letterSpacing: '.8px', color: 'rgba(255,255,255,.45)', textTransform: 'uppercase', marginTop: 3 }}>
                by Sustainability-Pros
              </div>
            </div>
          </Link>
          <Menu
            theme="dark"
            mode="inline"
            items={menuItems}
            selectedKeys={[selectedKey]}
            onClick={onMenuClick}
          />
        </Layout.Sider>

        <Layout>
          <Layout.Header style={{ display: 'flex', alignItems: 'center', gap: 16, boxShadow: '0 1px 4px rgba(0,21,41,.08)', zIndex: 2 }}>
            <Button type="text" onClick={() => navigate('/')} style={{ fontWeight: 500, paddingInline: 8 }}>
              <BankOutlined /> {title || (projectId ? 'Project' : 'Projects')} <DownOutlined style={{ fontSize: 10 }} />
            </Button>
            {/* NOTE: not wired to anything yet — no value, no handler. Hidden below lg so a
                non-functional field is not the thing eating an iPad's header width. */}
            {screens.lg && (
              <Input
                prefix={<SearchOutlined style={{ color: token.colorTextQuaternary }} />}
                placeholder="Search tools, documents, work orders…"
                aria-label="Search"
                style={{ maxWidth: 400 }}
              />
            )}
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 18 }}>
              <Badge dot color="#FFC107" offset={[-2, 2]}>
                <Button type="text" icon={<BellOutlined style={{ fontSize: 17 }} />} aria-label="Notifications" />
              </Badge>
              <Dropdown menu={{ items: accountMenu, onClick: onAccountClick }} trigger={['click']} placement="bottomRight">
                <Avatar style={{ backgroundColor: '#4CAF50', cursor: 'pointer', fontWeight: 600 }}>
                  {initialsOf(currentUser?.displayName, currentUser?.email)}
                </Avatar>
              </Dropdown>
            </div>
          </Layout.Header>

          <OfflineBanner />
          <InviteConflictBanner />
          {/* viewport-fit=cover lets the page run edge to edge in an installed PWA. The status bar
              is opaque (index.html), so the top needs nothing; the bottom inset keeps the last row
              of a table and its buttons above the iPad home indicator, and the side insets matter
              only on a notched phone. env() is 0 everywhere else, so the web layout is unchanged. */}
          <Layout.Content style={{
            padding: contentPad, overflow: 'auto',
            paddingBottom: `calc(${typeof contentPad === 'number' ? `${contentPad}px` : contentPad} + env(safe-area-inset-bottom))`,
            paddingLeft: `calc(${typeof contentPad === 'number' ? `${contentPad}px` : contentPad} + env(safe-area-inset-left))`,
            paddingRight: `calc(${typeof contentPad === 'number' ? `${contentPad}px` : contentPad} + env(safe-area-inset-right))`,
          }}>
            <Outlet />
          </Layout.Content>
        </Layout>
      </Layout>
    </ShellTitleCtx.Provider>
  );
};

export default AppShell;
